import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    makeStyles,
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    Select,
    Tooltip,
    tokens,
    useId,
} from "@fluentui/react-components";
import {
    EmojiRegular,
    PersonAccountsRegular,
    PersonRegular,
    SignOutRegular,
} from "@fluentui/react-icons";

import logoColor from '../Assets/Logos/logo-color.png';
import logoWhite from '../Assets/Logos/logo-white.png';
import useAvatar from "../Components/Avatar";
import { useHandleLogout } from '../Components/HandleLogout';
import { useSignInState } from '../Components/signInState';
import { useConfig } from '../context/ConfigContext';
import { login } from '../services/authService';
import { toggleSleek } from '../utils/sleek';

const useStyles = makeStyles({
    image: {
        columnGap: "15px",
        display: "inline-flex",
        justifyContent: "flex-start"
    },
    buttons: {
        columnGap: "8px",
        display: "inline-flex",
        justifyContent: "flex-end",
        alignItems: "center"
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
    },
    tenantSelector: {
        display: "inline-flex",
        alignItems: "center"
    },
    logoLink: {
        display: "inline-flex",
        textDecorationLine: "none",
        color: "inherit",
    },
    userMenuTrigger: {
        fontWeight: tokens.fontWeightRegular,
        columnGap: tokens.spacingHorizontalSNudge,
        color: tokens.colorNeutralForeground1,
        transition: "background-color 150ms ease-out",
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1,
        },
        "&:hover:active": {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
            color: tokens.colorNeutralForeground1,
        },
        "&:active": {
            color: tokens.colorNeutralForeground1,
        },
    },
    logoutMenuItem: {
        color: tokens.colorPaletteRedForeground1,
    },
});



export const Header = () => {
    const styles = useStyles();
    const selectId = useId();
    const [theme, setTheme] = useState<'light' | 'dark'>('dark');
    const { signInState, setSignInState, reloadWorkspaceForCustomer } = useSignInState();
    const { basecampUrl } = useConfig();
    const userInfo = signInState.userInfo;
    const displayName = useMemo(() => {
        if (!userInfo)
            return '';
        return userInfo.DisplayName || `${userInfo.FirstName ?? ''} ${userInfo.LastName ?? ''}`.trim() || userInfo.Email || '';
    }, [userInfo]);
    // Prefer UserName (sourced from preferred_username) over Email: the email claim can be
    // multi-valued in IdSrv when AspNetUsers.Email duplicates an AspNetUserClaims row, which
    // surfaces in the UI as a serialized JSON array.
    const displayIdentity = useMemo(() => userInfo?.UserName || userInfo?.Email || '', [userInfo]);

    const { handleLogout } = useHandleLogout();
    const navigate = useNavigate();

    useEffect(() => {
        setTheme((userInfo?.ThemePreference as 'light' | 'dark') || 'dark');
    }, [userInfo?.ThemePreference]);

    const avatar = useAvatar(undefined, displayName);

    const onLogin = () => {
        login();
    };

    const handleTenantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCompanyID = Number(event.target.value);
        const customers = signInState.customerInfo;
        setSignInState((prevState) => ({ ...prevState, currentCustomerID: selectedCompanyID }));
        void reloadWorkspaceForCustomer(selectedCompanyID, customers);
    };

    const logoImg = (
        <img src={theme === 'dark' ? logoWhite : logoColor} alt="Logo" style={{ marginLeft: '10px', marginTop: '10px', marginBottom: '10px', height: '30px', width: 'auto' }} />
    );

    return (
        <header>
            <div className={styles.container} style={{ paddingRight: '10px' }}>
                <a
                    href={basecampUrl}
                    target="_self"
                    className={styles.logoLink}
                >
                    {logoImg}
                </a>
                {signInState.customerInfo.length > 1 && (
                    <div className={styles.tenantSelector}>
                        <Select id={selectId} value={signInState.currentCustomerID} onChange={handleTenantChange}>
                            {
                                signInState.customerInfo.map((info, index) => (
                                    <option key={index} value={info.CustomerID}>
                                        {info.CompanyName}
                                    </option>
                                ))
                            }
                        </Select>
                    </div>
                )}
                <div className={styles.buttons}>
                    {userInfo ? (
                        <>
                            <Tooltip content="Send feedback" relationship="label">
                                <Button
                                    appearance="subtle"
                                    icon={<EmojiRegular />}
                                    onClick={toggleSleek}
                                />
                            </Tooltip>
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <Button
                                        appearance="transparent"
                                        className={styles.userMenuTrigger}
                                    >
                                        {displayIdentity || displayName}
                                        {avatar}
                                    </Button>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            icon={<PersonRegular />}
                                            onClick={() => navigate('/profile')}
                                        >
                                            Profile
                                        </MenuItem>
                                        <MenuItem
                                            icon={<SignOutRegular />}
                                            onClick={handleLogout}
                                            className={styles.logoutMenuItem}
                                        >
                                            Logout
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        </>
                    ) : (
                        <>
                            <Button size="medium" icon={<PersonAccountsRegular />} onClick={onLogin}>
                                Log in
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
};
